import { Box, Text, Icon, HStack, useDisclosure, useColorModeValue } from '@chakra-ui/react';
import { FaBell, FaBullhorn, FaWhatsapp } from 'react-icons/fa';
import { MdEventAvailable } from 'react-icons/md';
import {
	FiUserPlus,
	FiUsers,
	FiFlag,
	FiPlusCircle,
	FiClipboard,
	FiCheckSquare,
	FiAlertTriangle,
} from 'react-icons/fi';
import { format } from 'date-fns';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { NOTIFICATION_TYPES } from 'constants/notification.contants';
import { useReadNotification } from 'hooks/notification/useReadNotification';
import NotificationView from './NotificationView';

/* -------------------------------------------------------------------------- */
/* Per-type presentation: icon + accent colour + human title.                 */
/* -------------------------------------------------------------------------- */
// Each entry defines a single base hue; tints/borders derive from it so every
// type reads as its own colour (icon chip, left rail, title, card wash).
//
// TWO strengths of each hue exist because the card flips with the colour mode:
// `accent` is the bright value tuned for the dark surface, `ink` is the same
// hue darkened for the white light-mode card. The bright set alone measured
// 1.9–2.9:1 on white — every type label failed AA the moment light mode was
// on. Render sites pick between them with useColorModeValue.
const darken = (rgb, f = 0.62) =>
	rgb
		.split(',')
		.map((n) => Math.round(Number(n.trim()) * f))
		.join(', ');

const makeConfig = (icon, title, rgb) => ({
	icon,
	title,
	accent: `rgb(${rgb})`,
	ink: `rgb(${darken(rgb)})`,
	iconColor: `rgb(${rgb})`,
	iconInk: `rgb(${darken(rgb)})`,
	iconBg: `rgba(${rgb}, 0.16)`,
	tint: `rgba(${rgb}, 0.10)`,
	tintStrong: `rgba(${rgb}, 0.16)`,
	border: `rgba(${rgb}, 0.35)`,
});

const TYPE_CONFIG = {
	[NOTIFICATION_TYPES.ANNOUNCEMENT]: makeConfig(FaBullhorn, 'Announcement', '255, 149, 0'), // gold
	[NOTIFICATION_TYPES.LEAD_CREATED]: makeConfig(FiPlusCircle, 'New Lead', '11, 197, 234'), // cyan
	[NOTIFICATION_TYPES.LEAD_ASSIGNED]: makeConfig(FiUserPlus, 'Lead Assigned', '66, 153, 225'), // blue
	[NOTIFICATION_TYPES.LEAD_BULK_ASSIGNED]: makeConfig(FiUsers, 'Leads Assigned', '102, 126, 234'), // indigo
	[NOTIFICATION_TYPES.LEAD_CLAIMED]: makeConfig(FiFlag, 'Lead Claimed', '159, 122, 234'), // purple
	[NOTIFICATION_TYPES.INTERVIEW_INVITE]: makeConfig(MdEventAvailable, 'Interview Invite', '56, 178, 172'), // teal
	[NOTIFICATION_TYPES.TASK_ASSIGNED]: makeConfig(FiClipboard, 'Task Assigned', '237, 137, 54'), // orange
	[NOTIFICATION_TYPES.TASK_COMPLETED]: makeConfig(FiCheckSquare, 'Task Completed', '72, 187, 120'), // green
	// WhatsApp's own green, so a customer changing hands is recognisable in the
	// list without reading it. Handed TO you and taken FROM you are separate
	// entries: the same row would otherwise read as good news either way.
	[NOTIFICATION_TYPES.WHATSAPP_CHAT_ASSIGNED]: makeConfig(FaWhatsapp, 'WhatsApp Chat Assigned', '37, 211, 102'), // green
	[NOTIFICATION_TYPES.WHATSAPP_CHAT_TAKEN]: makeConfig(FaWhatsapp, 'WhatsApp Chat Moved', '113, 128, 150'), // slate
	[NOTIFICATION_TYPES.SYSTEM_ALERT]: makeConfig(FiAlertTriangle, 'System Alert', '245, 101, 101'), // red
};

const DEFAULT_CONFIG = makeConfig(FaBell, 'Notification', '255, 149, 0');

// Types that open the detail modal (they carry a full message / an action).
const MODAL_TYPES = [
	NOTIFICATION_TYPES.ANNOUNCEMENT,
	NOTIFICATION_TYPES.INTERVIEW_INVITE,
	NOTIFICATION_TYPES.SYSTEM_ALERT,
];

// Lead types that deep-link to the lead when a leadId is present.
const LEAD_TYPES = [
	NOTIFICATION_TYPES.LEAD_ASSIGNED,
	NOTIFICATION_TYPES.LEAD_BULK_ASSIGNED,
	NOTIFICATION_TYPES.LEAD_CLAIMED,
	NOTIFICATION_TYPES.LEAD_CREATED,
];

// Map the raw type to the `type` string NotificationView expects for its footer.
const getModalType = (type) => {
	if (type === NOTIFICATION_TYPES.ANNOUNCEMENT) return 'announcement';
	if (type === NOTIFICATION_TYPES.INTERVIEW_INVITE) return 'invite';
	return 'notification';
};

const NotificationBox = ({ data }) => {
	const notification = data?.notification;
	const { type, message, createdAt, metadata } = notification || {};

	const { isOpen, onOpen, onClose } = useDisclosure();
	const { readNotification } = useReadNotification();
	const navigate = useNavigate();

	const config = TYPE_CONFIG[type] || DEFAULT_CONFIG;
	const { icon, iconBg, tint, tintStrong, border, title } = config;

	// Bright hue on the dark card, darkened ink of the SAME hue on the light
	// card — the type identity survives the toggle, the contrast does too.
	const accent = useColorModeValue(config.ink, config.accent);
	const iconColor = useColorModeValue(config.iconInk, config.iconColor);

	const isUnread = !(data?.isRead ?? data?.read);

	const formattedDate = createdAt
		? format(new Date(createdAt), 'MMM d, yyyy · h:mm a')
		: '';

	const markAsRead = useCallback(
		async (id) => {
			try {
				if (id) await readNotification(id);
			} catch (error) {
				console.log(error);
			}
		},
		[readNotification],
	);

	const handleNotificationOpen = async () => {
		if (isUnread) await markAsRead(notification?._id);

		if (MODAL_TYPES.includes(type)) return onOpen();

		if (LEAD_TYPES.includes(type) && metadata?.leadId) {
			const basePath = metadata?.eLeadStatus === 'new' ? 'fresh-leads' : 'lead';
			return navigate(
				`/${basePath}?page=1&pageSize=40&lead=${metadata.leadId}`
			);
		}

		// Task / anything else with a readable message → show it in the modal.
		if (message) return onOpen();
	};

	return (
		<>
			<Box
				onClick={handleNotificationOpen}
				position='relative'
				w='full'
				cursor='pointer'
				role='button'
				borderRadius='xl'
				overflow='hidden'
				borderWidth='1px'
				borderColor={isUnread ? border : 'border.subtle'}
				bg={isUnread ? tint : 'transparent'}
				transition='all 0.2s ease'
				_hover={{
					bg: tintStrong,
					borderColor: border,
					transform: 'translateX(2px)',
				}}
			>
				{/* Type accent rail */}
				<Box
					position='absolute'
					left={0}
					top={0}
					bottom={0}
					w='3px'
					bg={accent}
				/>

				<HStack align='flex-start' spacing={3} pl={4} pr={3} py={3}>
					{/* Type icon chip */}
					<Box
						flexShrink={0}
						bg={iconBg}
						borderRadius='xl'
						boxSize={10}
						display='flex'
						alignItems='center'
						justifyContent='center'
						borderWidth='1px'
						borderColor={border}
						title={title}
					>
						<Icon as={icon} boxSize={5} color={iconColor} />
					</Box>

					{/* Message details */}
					<Box flex='1' minW={0}>
						<HStack justify='space-between' align='center' spacing={2} mb={0.5}>
							<Text
								fontSize='xs'
								fontWeight='bold'
								color={accent}
								textTransform='uppercase'
								letterSpacing='wide'
								noOfLines={1}
							>
								{title}
							</Text>
							{isUnread && (
								<Box
									flexShrink={0}
									w='7px'
									h='7px'
									borderRadius='full'
									bg={accent}
									boxShadow={`0 0 8px ${accent}`}
								/>
							)}
						</HStack>

						<Text
							fontSize='sm'
							color='text.body'
							fontWeight={isUnread ? 'semibold' : 'normal'}
							lineHeight='1.35'
							noOfLines={2}
							wordBreak='break-word'
						>
							{message || 'You have a new notification.'}
						</Text>

						{formattedDate && (
							<Text fontSize='11px' color='text.muted' mt={1}>
								{formattedDate}
							</Text>
						)}
					</Box>
				</HStack>
			</Box>

			{isOpen && (
				<NotificationView
					title={title}
					item={notification}
					type={getModalType(type)}
					isOpen={isOpen}
					onClose={onClose}
				/>
			)}
		</>
	);
};

export default NotificationBox;
